import { useState, useRef, useLayoutEffect, useCallback, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

type Position = 'top' | 'bottom' | 'left' | 'right';

const BaseTooltip = ({
  children,
  content,
  position = 'top',
  delay = 300,
  className = '',
  contentClassName = '',
  style = {},
}: {
  children: ReactNode;
  content?: ReactNode;
  position?: Position;
  delay?: number;
  className?: string;
  contentClassName?: string;
  style?: CSSProperties;
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; transform: string }>({
    top: 0,
    left: 0,
    transform: '',
  });
  const triggerRef = useRef<HTMLDivElement>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const m = 8;
    switch (position) {
      case 'bottom':
        setCoords({
          top: r.bottom + m,
          left: r.left + r.width / 2,
          transform: 'translateX(-50%)',
        });
        break;
      case 'top':
        setCoords({
          top: r.top - m,
          left: r.left + r.width / 2,
          transform: 'translate(-50%, -100%)',
        });
        break;
      case 'left':
        setCoords({
          top: r.top + r.height / 2,
          left: r.left - m,
          transform: 'translate(-100%, -50%)',
        });
        break;
      case 'right':
        setCoords({
          top: r.top + r.height / 2,
          left: r.right + m,
          transform: 'translateY(-50%)',
        });
        break;
      default:
        setCoords({
          top: r.bottom + m,
          left: r.left + r.width / 2,
          transform: 'translateX(-50%)',
        });
    }
  }, [position]);

  useLayoutEffect(() => {
    if (!isVisible) return;
    updatePosition();
    const onMove = () => updatePosition();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [isVisible, updatePosition]);

  const showTooltip = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    showTimerRef.current = setTimeout(() => {
      updatePosition();
      setIsVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    hideTimerRef.current = setTimeout(() => {
      setIsVisible(false);
    }, delay);
  };

  const tooltipNode =
    isVisible &&
    content &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        className={clsx(
          'pointer-events-none fixed z-[9999] rounded-3xl bg-blue px-3 py-2 text-xs text-white font-medium transition-opacity duration-200',
          contentClassName,
        )}
        style={{
          top: coords.top,
          left: coords.left,
          transform: coords.transform,
          ...style,
        }}
      >
        {content}
      </div>,
      document.body,
    );

  useLayoutEffect(() => {
    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  return (
    <>
      <div
        ref={triggerRef}
        className={`relative inline-block ${className}`}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        {children}
      </div>
      {tooltipNode}
    </>
  );
};

export default BaseTooltip;
